/**
 * Demo participants: agent bots in the open project, so the multiplayer interface — the header's avatars,
 * pointers on the plan and in 3D, a hold on an item, following someone's view — can be seen and tried before
 * the rt server is connected (docs/collaboration.md «Demo bots»). Each bot keeps to a script: a spell on the
 * floor plan with its pointer over the rooms and the furniture, a spell orbiting the house, a spell walking a
 * room and looking round what it points at; the editors among them try an item somewhere else. They read the
 * document and never write it: a hold is drawn by the overlay, the scene stays as it is. The agent chat's subagents
 * at work join as participants of their own (`showAgents`); a run that borrows the presence brings only them.
 */
import * as THREE from 'three';
import type { Item, Level, Room, Vec2 } from '../core/hp/types';
import type { Store } from '../state/store';
import type { CameraPose } from '../render/three/Viewer';
import { labelCenter } from '../state/minimap';
import { containsPoint } from '../state/sceneGesture';
import { capturePresence } from './presence';
import type { CollaborationState, PresenceEvent, PresenceInput, PresenceViewport, SharedSnapshot, SelectionTarget, WorldPoint } from './protocol';
import type { ParticipantHold, ParticipantLook, ParticipantOwner, PresenceSource } from './presenceSource';
import { t as tr, type TranslationKey } from '../i18n/I18n';
import type { CursorState } from '../agent/cursor';
import { ROLES, roleById, type RoleId } from '../agent/roles';
import type { LinkAccess } from './share/source';

export const DEMO_BOTS_KEY = 'hp.demo.bots';
export function readDemoBots(): boolean {
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('demo') === 'presence' || localStorage.getItem(DEMO_BOTS_KEY) === '1'; } catch { return false; }
}
export function writeDemoBots(on: boolean): void {
  try { if (on) localStorage.setItem(DEMO_BOTS_KEY, '1'); else localStorage.removeItem(DEMO_BOTS_KEY); } catch { /* the choice lasts this page only */ }
}

/**
 * Whether the bots are in the project, and the presence the editor made for them: one switch for the «Ещё» menu,
 * the share dialog and the agent chat's simulation. The user's choice is remembered in this browser; the simulation
 * only borrows the bots (`lease`) for its run and gives them back, so a page reload does not bring them.
 */
class DemoBotsSwitch {
  private chosen = readDemoBots();
  private leases = new Set<symbol>();
  private presence: DemoPresence | null = null;
  private listeners = new Set<() => void>();
  get = (): boolean => this.chosen || this.leases.size > 0;
  /** The user's own choice, without the runs that borrow the presence: only then do the scripted bots and the people come. */
  chosenNow = (): boolean => this.chosen;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private notify(): void { for (const listener of this.listeners) listener(); }
  /** The user's choice. Off sends the scripted bots and the people away; a run keeps its subagents until it gives them back. */
  set = (on: boolean): void => {
    if (on === this.chosen) return;
    writeDemoBots(on); this.chosen = on;
    this.notify();
  };
  /** The presence for a while, without remembering it; the returned function gives it back. */
  lease(): () => void {
    const token = Symbol('lease');
    this.leases.add(token); this.notify();
    return () => { if (this.leases.delete(token)) this.notify(); };
  }
  /** The running presence, once the editor has made one for the bots; null without an editor or during a shared session. */
  current = (): DemoPresence | null => this.presence;
  attach(presence: DemoPresence | null): void { if (presence !== this.presence) { this.presence = presence; this.notify(); } }
  /** The presence as soon as it runs, or null after `timeout` ms (no editor mounted, a shared session open). */
  ready(timeout = 1500): Promise<DemoPresence | null> {
    if (this.presence) return Promise.resolve(this.presence);
    return new Promise(resolve => {
      const stop = this.subscribe(() => { if (this.presence) { stop(); clearTimeout(timer); resolve(this.presence); } });
      const timer = setTimeout(() => { stop(); resolve(this.presence); }, timeout);
    });
  }
}
export const demoBots = new DemoBotsSwitch();

export type DemoRole = 'editor' | 'viewer';
export interface DemoMember {
  id: string; name: string; color: string; agent: boolean; role: DemoRole;
  icon?: string; avatar?: string; halo?: string; ink?: string; owner?: ParticipantOwner;
  /** A person asked in through the invitation field, rather than one of the agents. */
  email?: string;
  /** False from the invitation until the person «joins» a moment later. */
  online: boolean;
}

/** Someone asking the owner for a role; `email` brings in someone outside the project, as an invitation would. */
export interface DemoRequest { id: string; name: string; wants: DemoRole; email?: string }

/** What a bot can be sent to work on (`DemoPresence.direct`). */
export type DemoTarget = { kind: 'item'; id: string } | { kind: 'room'; index: number } | { kind: 'wall'; id: string } | { kind: 'opening'; id: string } | { kind: 'point'; point: WorldPoint };
/** A task for a bot: where its pointer goes and rests, what its pill says, whether an editor holds the item meanwhile. */
export interface DemoTask { target: DemoTarget; activity: string; hold?: boolean; /** False rests the bot on the target: no pulse. */ working?: boolean;
  /** Where on the plan the pointer rests instead of the target's own point; the target is still what it holds. */ at?: Vec2 }

type Kind = 'floor' | 'orbit' | 'walk';
/** What a bot's pill says in each spell, as keys; `item` takes the item's name as {0}. */
interface Words { floor: TranslationKey; orbit: TranslationKey; walk: TranslationKey; item: TranslationKey }
interface Stop { point: WorldPoint; arrive: number; leave: number; activity: string; target?: SelectionTarget; itemId?: string; trying?: Vec2 }
interface Phase {
  kind: Kind; start: number; end: number; stops: Stop[]; activity: string;
  plan?: { centre: Vec2; span: { width: number; height: number } };
  orbit?: { target: THREE.Vector3; distance: number; elevation: number; from: number; to: number; wallMode: 'full' | 'cutaway' };
  walk?: { position: Vec2; roomIndex: number };
}
interface Bot { member: DemoMember; words: Words; order: number; rng: () => number; seq: number; phase: Phase | null; kinds: Kind[];
  yaw: number; pitch: number; lastTick: number; activity: string; joinAt: number; working: boolean; directed: boolean;
  /** A task from the agent chat's simulation: it overrides the script's stops until it is lifted. */
  directive: (DemoTask & { since: number; from: WorldPoint | null }) | null;
  lastPoint: WorldPoint | null }

const SCRIPT_WORDS: Partial<Record<RoleId, Words>> = {
  layout: { floor: '#DemoBotLayoutPlan', orbit: '#DemoBotLayoutOrbit', walk: '#DemoBotLayoutWalk', item: '#DemoBotLayoutItem' },
  catalog: { floor: '#DemoBotCatalogPlan', orbit: '#DemoBotCatalogOrbit', walk: '#DemoBotCatalogWalk', item: '#DemoBotCatalogItem' },
  style: { floor: '#DemoBotStylePlan', orbit: '#DemoBotStyleOrbit', walk: '#DemoBotStyleWalk', item: '#DemoBotStyleItem' },
  decor: { floor: '#DemoBotDecorPlan', orbit: '#DemoBotDecorOrbit', walk: '#DemoBotDecorWalk', item: '#DemoBotDecorItem' },
};
/**
 * The four agents of the design's «Subagents» frame (Layout, Catalog, Style and Decor Agent), the first four roles of
 * the roster (`roles.ts`) with the words of their script; shared by the bots and the chat's simulation.
 */
export const DEMO_AGENTS: ReadonlyArray<Pick<DemoMember, 'id' | 'name' | 'color'> & { role: RoleId; icon: string; halo: string; words: Words }> =
  ROLES.slice(0, 4).map(role => ({ id: 'bot:' + role.id, role: role.id, get name() { return role.name; }, color: role.color, icon: role.icon, halo: role.halo, words: SCRIPT_WORDS[role.id]! }));
const AGENTS = DEMO_AGENTS;
const PERSON_COLORS = ['#742AED'];
const DEMO_PEOPLE: DemoMember[] = [
  { id: 'person:mark', name: 'Mark', color: '#742AED', agent: false, role: 'editor', online: true, avatar: '/hp/ui/presence/mark.png' },
  { id: 'person:mason', name: 'Mason', color: '#93C5FD', ink: '#000000', agent: false, role: 'viewer', online: true, avatar: '/hp/ui/presence/mason.png' },
];
export type DemoView = 'auto' | Kind;
export type DemoPlayback = { paused: boolean; view: DemoView; crowd: number; selection: 'auto' | 'item' | 'wall' | 'opening' };
/** The people the Share dialog's search finds in the demo; an invitation by their address joins like anyone's. */
export const DEMO_ACCOUNTS: ReadonlyArray<{ name: string; email: string }> = [
  { name: 'Anna Petrova', email: 'anna.petrova@example.com' }, { name: 'Lucas Moreau', email: 'lucas.moreau@example.com' }, { name: 'Priya Shah', email: 'priya.shah@example.com' },
];
/** Whom «View as a guest» makes you: the demo's guest of the project. */
export const DEMO_GUEST = 'person:mason';
const PERSON_WORDS: Words = { floor: '#DemoBotPersonPlan', orbit: '#DemoBotPersonOrbit', walk: '#DemoBotPersonWalk', item: '#DemoBotPersonItem' };
const SELF = { userId: 'demo:self', role: 'owner' as const, get displayName() { return tr('#ShareYou'); } };
const EYE = 1.6;

/** mulberry32: the same bot walks the same way in the same project. */
function random(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const hash = (text: string) => { let h = 2166136261; for (const char of text) h = Math.imul(h ^ char.charCodeAt(0), 16777619); return h >>> 0; };
const ease = (value: number) => { const t = Math.min(1, Math.max(0, value)); return t * t * (3 - 2 * t); };
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
const turn = (from: number, to: number, t: number) => from + Math.atan2(Math.sin(to - from), Math.cos(to - from)) * t;

function levelBox(level: Level): { minX: number; maxX: number; minZ: number; maxZ: number } {
  const points = [...level.rooms.flatMap(room => room.polygon), ...level.walls.flatMap(wall => [wall.start, wall.end])];
  if (!points.length) return { minX: -3, maxX: 3, minZ: -2, maxZ: 2 };
  return { minX: Math.min(...points.map(p => p.x)), maxX: Math.max(...points.map(p => p.x)),
    minZ: Math.min(...points.map(p => p.z)), maxZ: Math.max(...points.map(p => p.z)) };
}
/** Where a pointer rests on an item: the middle of its top, on the plan its middle on the floor. */
function itemPoint(item: Item, plan: boolean): WorldPoint {
  const top = item.pivot === 'center' ? item.elevation + item.size.h / 2 : item.elevation + item.size.h;
  return { x: item.position.x, y: plan ? 0 : Math.max(.05, top), z: item.position.z };
}
function itemName(item: Item): string {
  const caption = (item.caption ?? '').trim();
  if (!caption || caption.length > 28 || /^[\w-]+\/\d+/.test(caption)) return tr('#DemoBotItem');
  return caption.slice(0, 1).toLocaleLowerCase() + caption.slice(1);
}
const roomPoint = (room: Room): WorldPoint => { const c = labelCenter(room.polygon); return { x: c.x, y: 0, z: c.z }; };

/**
 * The demo bots as a presence source: what a real session would hear from the server, made up locally and
 * sampled at the real transport's 12 Hz rate. `start()` runs the bots, `stop()` rests them.
 */
export class DemoPresence implements PresenceSource {
  readonly clientId = 'demo:self';
  readonly kind = 'demo';
  private bots: Bot[] = [];
  private listeners = new Set<() => void>();
  private memberListeners = new Set<() => void>();
  private frame = 0;
  private state: CollaborationState = { status: 'ready', snapshot: null, participants: [], previews: [], error: null, pendingCommandId: null };
  private currentHolds: ParticipantHold[] = [];
  private snapshotFor: { scene: unknown; name: string; projectId: string } | null = null;
  private levelKey = '';
  private memberList: readonly DemoMember[] = [];
  private rig = new THREE.PerspectiveCamera();
  private playback: DemoPlayback = { paused: false, view: 'auto', crowd: 6, selection: 'auto' };
  private playbackListeners = new Set<() => void>();
  private clock = 0;
  private lastTime: number | null = null;
  private requestList: readonly DemoRequest[] = [];
  private viewer: string | null = null;
  /** Who the Share dialog's link admits in the demo. */
  link: LinkAccess = 'invited';

  /** Bots the chat's subagents brought, gone when their cursor goes. */
  private onDemand = new Set<string>();
  /** Scripted bots a subagent directs right now. */
  private directed = new Set<string>();

  /** `scripted: false` is a run's presence: no scripted agents and no people, only the subagents at work (`showAgents`). */
  constructor(private readonly options: { store: Store; now?: () => number; people?: boolean; scripted?: boolean }) {
    const project = options.store.get().projectId, scripted = options.scripted !== false;
    if (scripted) AGENTS.forEach((agent, order) => {
      this.bots.push(this.bot({ id: agent.id, name: agent.name, color: agent.color, icon: agent.icon, ink: '#FFFFFF', halo: agent.halo,
        agent: true, role: order === 3 ? 'viewer' : 'editor', online: true }, agent.words, order, project, 0));
    });
    if (scripted && options.people) DEMO_PEOPLE.forEach((person, index) => this.bots.push(this.bot({ ...person }, PERSON_WORDS, index + 6, project, 0)));
    const decor = AGENTS[3], outsider = DEMO_ACCOUNTS[1];
    if (scripted) this.requestList = [{ id: decor.id, name: decor.name, wants: 'editor' }, { id: `guest:${outsider.email}`, name: outsider.name, email: outsider.email, wants: 'viewer' }];
    this.publishMembers();
  }

  private bot(member: DemoMember, words: Words, order: number, project: string, joinAt: number): Bot {
    const cycle: Kind[] = ['floor', 'orbit', 'walk'];
    const kinds = [...cycle.slice(order % 3), ...cycle.slice(0, order % 3)];
    return { member, words, order, rng: random(hash(member.id + ':' + project)), seq: 0, phase: null, kinds, yaw: order * 1.3, pitch: -.1,
      lastTick: 0, activity: tr(words.floor), joinAt, working: false, directed: false, directive: null, lastPoint: null };
  }
  private now(): number { return this.clock; }

  /** False for a chat run's presence: no script, no people, only the subagents at work. */
  get scripted(): boolean { return this.options.scripted !== false; }
  getPlayback = (): DemoPlayback => this.playback;
  subscribePlayback = (listener: () => void): (() => void) => { this.playbackListeners.add(listener); return () => { this.playbackListeners.delete(listener); }; };
  configure(patch: Partial<DemoPlayback>): void {
    const changedView = patch.view !== undefined && patch.view !== this.playback.view || patch.selection !== undefined && patch.selection !== this.playback.selection;
    this.playback = { ...this.playback, ...patch };
    if (changedView) for (const bot of this.bots) bot.phase = null;
    for (const listener of this.playbackListeners) listener();
    this.tick(this.lastTime ?? (this.options.now ?? (() => performance.now()))());
  }

  get = (): CollaborationState => this.state;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  /** The bots do not watch the user: the local pointer goes nowhere. */
  sendCursor(_input: Pick<PresenceInput, 'floor' | 'mode' | 'cursor' | 'presence' | 'viewport' | 'selection'>): void {}
  retry(): Promise<void> { return Promise.resolve(); }
  look(userId: string): ParticipantLook | null {
    const bot = this.bots.find(candidate => candidate.member.id === userId);
    return bot ? { ...bot.member, activity: bot.activity, working: bot.working, directed: bot.directed } : null;
  }
  holds(): readonly ParticipantHold[] { return this.currentHolds; }

  /**
   * Sends a bot to work on something (the agent chat's simulation): its pointer glides there and rests, its pill says
   * what it does, an editor holds the item. The bot keeps to the plan meanwhile, where the work is. Null lets it roam
   * again. False when there is no such bot.
   */
  direct(botId: string, task: DemoTask | null): boolean {
    const bot = this.bots.find(candidate => candidate.member.id === botId);
    if (!bot) return false;
    if (!task) { bot.directive = null; return true; }
    bot.directive = { ...task, since: this.now(), from: bot.lastPoint };
    if (bot.phase && bot.phase.kind !== 'floor') bot.phase = null;
    return true;
  }

  /**
   * The chat's subagents as they stand (`agentCursor.all()`): each role at work is its scripted bot, else one that joins
   * for it; a role whose cursor is gone leaves, and a scripted bot roams again.
   */
  showAgents(entries: readonly CursorState[]): void {
    const now = new Set<string>();
    let joined = false;
    for (const entry of entries) {
      const role = roleById(entry.key);
      if (!role || !entry.visible || !entry.point) continue;
      const id = 'bot:' + role.id;
      now.add(id);
      let bot = this.bots.find(candidate => candidate.member.id === id);
      if (!bot) {
        bot = this.bot({ id, name: role.name, color: role.color, icon: role.icon, halo: role.halo, ink: '#FFFFFF', agent: true, role: 'editor', online: true },
          PERSON_WORDS, this.bots.length, this.options.store.get().projectId, 0);
        this.bots.push(bot); this.onDemand.add(id); joined = true;
      }
      const held = !!(entry.item && entry.hold);
      const target: DemoTarget = held ? { kind: 'item', id: entry.item! } : { kind: 'point', point: { x: entry.point.x, y: 0, z: entry.point.z } };
      const task: DemoTask = { target, activity: entry.label, hold: entry.hold, working: entry.working, ...(held ? { at: { x: entry.point.x, z: entry.point.z } } : {}) };
      if (bot.directive && samePlace(bot.directive, task)) bot.directive = { ...bot.directive, ...task };
      else this.direct(id, task);
    }
    for (const id of this.directed) if (!now.has(id) && !this.onDemand.has(id)) this.direct(id, null);
    const gone = [...this.onDemand].filter(id => !now.has(id));
    if (gone.length) { this.bots = this.bots.filter(bot => !gone.includes(bot.member.id)); for (const id of gone) this.onDemand.delete(id); }
    this.directed = new Set([...now].filter(id => !this.onDemand.has(id)));
    if (joined || gone.length) { this.publishMembers(); if (this.lastTime !== null) this.tick(this.lastTime); }
  }

  /** The share dialog's list: every bot and invitee, stable between changes. */
  members = (): readonly DemoMember[] => this.memberList;
  subscribeMembers = (listener: () => void): (() => void) => { this.memberListeners.add(listener); return () => { this.memberListeners.delete(listener); }; };
  private publishMembers(): void {
    this.memberList = this.bots.map(bot => ({ ...bot.member }));
    this.snapshotFor = null;
    for (const listener of this.memberListeners) listener();
  }
  setRole(id: string, role: DemoRole): void {
    const bot = this.bots.find(candidate => candidate.member.id === id);
    if (!bot || bot.member.role === role) return;
    bot.member = { ...bot.member, role };
    this.publishMembers();
  }
  remove(id: string): void {
    const before = this.bots.length;
    this.bots = this.bots.filter(bot => bot.member.id !== id);
    this.requestList = this.requestList.filter(asked => asked.id !== id);
    if (this.bots.length !== before) { this.publishMembers(); this.tick(this.lastTime ?? 0); }
  }
  /** A person asked in by address: listed at once as invited, in the project a moment later. Null when the address is not one. */
  invite(email: string, role: DemoRole = 'editor'): DemoMember | null {
    const address = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) || this.bots.some(bot => bot.member.email === address)) return null;
    const local = address.split('@')[0].replace(/[._-]+/g, ' ').trim();
    const name = local ? local.split(' ').map(word => word.slice(0, 1).toLocaleUpperCase() + word.slice(1)).join(' ') : address;
    const people = this.bots.filter(bot => !bot.member.agent).length, order = this.bots.length;
    const member: DemoMember = { id: `guest:${address}`, name, color: PERSON_COLORS[people % PERSON_COLORS.length], agent: false, role, email: address, online: false };
    this.bots.push(this.bot(member, PERSON_WORDS, order, this.options.store.get().projectId, this.now() + 1600));
    this.requestList = this.requestList.filter(asked => asked.id !== member.id);
    this.publishMembers();
    return { ...member };
  }

  /** Who asks the owner for a role, oldest first; heard through `subscribeMembers`. */
  requests = (): readonly DemoRequest[] => this.requestList;
  /** A member asks to edit, or someone outside to come in; false when they have that role already or ask already. */
  ask(request: DemoRequest): boolean {
    const member = this.bots.find(bot => bot.member.id === request.id)?.member;
    if (member?.role === request.wants || member && request.wants === 'viewer' || this.requestList.some(asked => asked.id === request.id)) return false;
    this.requestList = [...this.requestList, request];
    this.publishMembers();
    return true;
  }
  /** Grants what was asked: a member's role at once, someone outside in as an invitee who joins a moment later. */
  approve(id: string): boolean {
    const request = this.requestList.find(asked => asked.id === id);
    if (!request) return false;
    this.requestList = this.requestList.filter(asked => asked !== request);
    const before = this.memberList;
    if (this.bots.some(bot => bot.member.id === id)) this.setRole(id, request.wants);
    else if (request.email) this.invite(request.email, request.wants);
    if (this.memberList === before) this.publishMembers();
    return true;
  }
  deny(id: string): boolean {
    const before = this.requestList.length;
    this.requestList = this.requestList.filter(asked => asked.id !== id);
    if (this.requestList.length === before) return false;
    this.publishMembers();
    return true;
  }
  /** The member you are while viewing as them: their bot leaves the room meanwhile. */
  viewAs(id: string | null): void { this.viewer = id; }

  /** Extra read-only participants for checking navigation under a realistic crowd. */
  setCrowd(count: number): void {
    count = Math.max(6, Math.min(100, Math.round(count)));
    this.bots = this.bots.filter(bot => !bot.member.id.startsWith('crowd:'));
    const mason = this.bots.find(bot => bot.member.id === 'person:mason')?.member, first = this.bots.length;
    for (let index = first; index < count; index++) {
      const agent = AGENTS[index % AGENTS.length], key = agent.id.slice(4);
      // The first of the crowd is Mason's own agent, as someone else's agent shows in the list.
      const owner = index === first && mason ? { name: mason.name, color: mason.color, ink: mason.ink, avatar: mason.avatar } : undefined;
      const member: DemoMember = { id: `crowd:${index}`, name: owner ? tr('#WebAgentOf', owner.name) : `${agent.name} ${index + 1}`, color: agent.color,
        agent: true, role: owner ? 'viewer' : 'editor', online: true, icon: agent.icon, ink: '#FFFFFF', halo: `/hp/ui/presence/${key}-halo.svg`, ...(owner ? { owner } : {}) };
      this.bots.push(this.bot(member, agent.words, index, this.options.store.get().projectId, 0));
    }
    this.publishMembers(); this.configure({ crowd: count });
  }

  start(): void {
    if (this.frame || typeof requestAnimationFrame !== 'function') return;
    let sampled = -Infinity;
    const step = (now: number) => {
      this.frame = requestAnimationFrame(step);
      if (this.playback.paused) { this.lastTime = now; return; }
      // Network-like 12 Hz snapshots; the overlay interpolates them at the display's refresh rate.
      if (now - sampled >= 1000 / 12) { sampled = now; this.tick(now); }
    };
    this.frame = requestAnimationFrame(step);
  }
  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.state = { ...this.state, participants: [] }; this.currentHolds = [];
    for (const listener of this.listeners) listener();
  }

  /** One step of every bot: where it points, what it looks through, what it holds. Public for the tests. */
  tick(time: number): void {
    if (!this.playback.paused && this.lastTime !== null) this.clock += Math.max(0, time - this.lastTime);
    this.lastTime = time;
    const now = this.clock;
    const store = this.options.store, editor = store.get(), level = store.level;
    if (!editor.scene || !level) return;
    this.refreshSnapshot(editor.scene, editor.projectName, editor.projectId);
    const key = JSON.stringify([editor.projectId, editor.activeLevelIndex, level.rooms.length, level.walls.length]);
    if (key !== this.levelKey) { this.levelKey = key; for (const bot of this.bots) bot.phase = null; }
    const participants: PresenceEvent[] = [], holds: ParticipantHold[] = [];
    let joined = false;
    for (const bot of this.bots) {
      if (bot.member.id === this.viewer) continue;
      if (!bot.member.online) {
        if (now < bot.joinAt) continue;
        bot.member = { ...bot.member, online: true }; joined = true;
      }
      if (!bot.phase || now >= bot.phase.end) bot.phase = this.phase(bot, level, now);
      participants.push(this.event(bot, level, now, editor.activeLevelIndex, holds));
    }
    if (joined) this.publishMembers();
    this.currentHolds = holds;
    this.state = { status: 'ready', snapshot: this.state.snapshot, participants, previews: [], error: null, pendingCommandId: null };
    for (const listener of this.listeners) listener();
  }

  private refreshSnapshot(scene: NonNullable<ReturnType<Store['get']>['scene']>, name: string, projectId: string): void {
    const previous = this.snapshotFor;
    if (previous && previous.scene === scene && previous.name === name && previous.projectId === projectId && this.state.snapshot) return;
    this.snapshotFor = { scene, name, projectId };
    const snapshot: SharedSnapshot = { documentId: `demo:${projectId}`, revision: 0, layoutVersion: 0, generation: 1, self: SELF,
      members: [SELF, ...this.bots.map(bot => ({ userId: bot.member.id, role: 'editor' as const, displayName: bot.member.name }))], name, scene };
    this.state = { ...this.state, snapshot };
  }

  /** The next spell of a bot's script, laid out on the level as it stands now. */
  private phase(bot: Bot, level: Level, now: number): Phase {
    const rng = bot.rng, previous = bot.phase?.kind;
    const kind = this.playback.view !== 'auto' ? this.playback.view : bot.directive ? 'floor' : previous ? bot.kinds[(bot.kinds.indexOf(previous) + 1) % bot.kinds.length] : bot.kinds[0];
    const duration = 9500 + rng() * 4500, box = levelBox(level);
    const centre = { x: (box.minX + box.maxX) / 2, z: (box.minZ + box.maxZ) / 2 };
    const rooms = level.rooms.filter(room => room.polygon.length >= 3);
    const items = level.items.filter(item => item.placement !== 'ceiling' && Number.isFinite(item.position.x) && Number.isFinite(item.position.z));
    const shuffled = <T,>(values: T[]) => values.map(value => [rng(), value] as const).sort((a, b) => a[0] - b[0]).map(([, value]) => value);
    const phase: Phase = { kind, start: now, end: now + duration, stops: [], activity: tr(bot.words[kind]) };
    // Give each participant a different part of the floor; six pointers on the same sofa hide their names.
    const axis = box.maxX - box.minX > box.maxZ - box.minZ ? 'x' : 'z';
    const sorted = [...items].sort((a, b) => a.position[axis] - b.position[axis]);
    const rank = this.bots.indexOf(bot), size = this.bots.length;
    const assigned = sorted.slice(Math.floor(rank * sorted.length / size), Math.floor((rank + 1) * sorted.length / size));
    let pool = shuffled(assigned.length ? assigned : items);
    if (kind === 'walk') {
      // The room with the most furniture that has a place to stand in it, else any room.
      const withItems = shuffled(rooms).map(room => ({ room, inside: items.filter(item => containsPoint(room.polygon, item.position)) }))
        .sort((a, b) => b.inside.length - a.inside.length);
      const chosen = withItems[bot.order % withItems.length];
      if (chosen) {
        const stand = labelCenter(chosen.room.polygon);
        phase.walk = { position: stand, roomIndex: chosen.room.index };
        pool = shuffled(chosen.inside);
      }
    } else if (kind === 'orbit') {
      const span = Math.max(box.maxX - box.minX, box.maxZ - box.minZ, 4);
      const from = Math.PI / 2 + .4 + bot.order * 1.6 + rng() * .4;
      phase.orbit = { target: new THREE.Vector3(centre.x, .4, centre.z), distance: span * 1.05 + 4.5, elevation: .82 + rng() * .22,
        from, to: from + (rng() < .5 ? 1 : -1) * (.45 + rng() * .45), wallMode: bot.order % 2 ? 'cutaway' : 'full' };
    } else {
      phase.plan = { centre, span: { width: (box.maxX - box.minX) * 1.25 + 2, height: (box.maxZ - box.minZ) * 1.25 + 2 } };
    }
    // The stops: furniture first, a room between; a pointer moves 0.9–1.6 s and rests 1.3–2.6 s.
    let t = 250 + rng() * 350, index = 0;
    const plan = kind === 'floor';
    while (t < duration - 1800) {
      let item = pool.length && (rng() < .72 || !rooms.length) ? pool[index++ % pool.length] : undefined;
      const requested = this.playback.selection !== 'auto' ? this.playback.selection
        : phase.stops.length % 2 === 0 && bot.member.role === 'editor' ? bot.order % 3 === 0 ? 'wall' : bot.order % 3 === 2 ? 'opening' : 'item' : 'item';
      const opening = requested === 'opening' ? level.openings[(bot.order + phase.stops.length) % level.openings.length] : undefined;
      const wall = requested === 'wall' ? level.walls.filter(wall => !wall.hidden)[(bot.order + phase.stops.length) % level.walls.filter(wall => !wall.hidden).length] : undefined;
      const target: SelectionTarget | undefined = opening ? { kind: 'opening', id: opening.id } : wall ? { kind: 'wall', id: wall.id } : item ? { kind: 'item', id: item.id } : undefined;
      if (opening || wall) item = undefined;
      const room = !item && rooms.length ? (phase.walk ? rooms.find(value => value.index === phase.walk!.roomIndex) : rooms[bot.order % rooms.length]) : undefined;
      let point: WorldPoint;
      if (target && (opening || wall)) point = demoTargetPoint(target, level)!;
      else if (item) point = itemPoint(item, plan);
      else if (room) {
        const c = roomPoint(room), jitter = { x: (rng() - .5) * 1.2, z: (rng() - .5) * 1.2 };
        point = containsPoint(room.polygon, { x: c.x + jitter.x, z: c.z + jitter.z }) ? { ...c, x: c.x + jitter.x, z: c.z + jitter.z } : c;
      } else point = { x: centre.x + (rng() - .5) * 2, y: 0, z: centre.z + (rng() - .5) * 2 };
      if (plan) point = { ...point, y: 0 };
      const move = 900 + rng() * 700, dwell = 1300 + rng() * 1300;
      const trying = item && bot.member.role === 'editor' && item.placement === 'floor' && dwell > 1700 && rng() < .55
        ? (() => { const angle = rng() * Math.PI * 2, reach = .35 + rng() * .4; return { x: Math.cos(angle) * reach, z: Math.sin(angle) * reach }; })() : undefined;
      phase.stops.push({ point, arrive: t + move, leave: t + move + dwell, target, itemId: item?.id, trying,
        activity: opening ? tr('#DemoBotCheckingOpening') : wall ? tr('#DemoBotCheckingWall') : item ? tr(bot.words.item, itemName(item)) : tr(bot.words[kind]) });
      t += move + dwell;
    }
    return phase;
  }

  /** The bot at `now`: its pointer, its view, and what it holds. */
  private event(bot: Bot, level: Level, now: number, slot: number, holds: ParticipantHold[]): PresenceEvent {
    const phase = bot.phase!, t = now - phase.start, dt = bot.lastTick ? Math.min(.1, (now - bot.lastTick) / 1000) : 0;
    bot.lastTick = now;
    let point: WorldPoint | null = phase.stops[0]?.point ?? null, stop: Stop | null = null, from = point, travelling = false;
    const directive = bot.directive, aimed = directive ? demoTargetPoint(directive.target, level) : null;
    const directed = aimed && directive!.at ? { ...aimed, x: directive!.at.x, z: directive!.at.z } : aimed;
    if (directive && directed) {
      // Sent to work: glide there in 0.9 s and rest on it; the script's stops wait.
      const origin = directive.from ?? directed, k = ease((now - directive.since) / 900);
      point = { x: mix(origin.x, directed.x, k), y: mix(origin.y, directed.y, k), z: mix(origin.z, directed.z, k) };
      if (k >= 1) point = { x: point.x + Math.sin(now / 430 + bot.order) * .025, y: point.y, z: point.z + Math.cos(now / 510 + bot.order) * .025 };
      const held = directive.hold && directive.target.kind === 'item' ? level.items.find(candidate => candidate.id === (directive.target as { id: string }).id) : undefined;
      if (held && bot.member.role === 'editor') holds.push({ key: `${bot.member.id}:${held.id}`, userId: bot.member.id, color: bot.member.color, item: held, trying: false });
    } else for (let i = 0; i < phase.stops.length; i++) {
      const current = phase.stops[i], start = i ? phase.stops[i - 1].leave : 0;
      if (t < current.arrive) { travelling = i > 0; const k = ease((t - start) / (current.arrive - start)); point = from && { x: mix(from.x, current.point.x, k), y: mix(from.y, current.point.y, k), z: mix(from.z, current.point.z, k) }; break; }
      if (t <= current.leave) { stop = current; point = current.point; break; }
      from = current.point; point = current.point;
    }
    // A resting hand still moves a little.
    if (point && stop) point = { x: point.x + Math.sin(now / 430 + bot.order) * .025, y: point.y, z: point.z + Math.cos(now / 510 + bot.order) * .025 };
    bot.lastPoint = point;
    const item = stop?.itemId ? level.items.find(candidate => candidate.id === stop!.itemId) : undefined;
    if (stop && item && bot.member.role === 'editor') {
      holds.push({ key: `${bot.member.id}:${item.id}`, userId: bot.member.id, color: bot.member.color, item, trying: false });
      if (stop.trying) {
        const u = (t - stop.arrive) / (stop.leave - stop.arrive);
        const k = u < .2 ? 0 : u < .45 ? ease((u - .2) / .25) : u < .7 ? 1 : u < .95 ? 1 - ease((u - .7) / .25) : 0;
        if (k > .02) {
          const offset = { x: stop.trying.x * k, z: stop.trying.z * k };
          holds.push({ key: `${bot.member.id}:${item.id}:try`, userId: bot.member.id, color: bot.member.color, trying: true,
            item: { ...item, position: { x: item.position.x + offset.x, z: item.position.z + offset.z } } });
          if (point) point = { ...point, x: point.x + offset.x, z: point.z + offset.z };
        }
      }
    }
    const selected = directive && directed
      ? directive.hold && directive.target.kind !== 'point' ? directive.target : null
      : stop?.target;
    const selection: SelectionTarget[] = selected && bot.member.role === 'editor' ? [selected] : [];
    bot.activity = directive && directed ? directive.activity : stop?.activity ?? phase.activity;
    bot.working = directive && directed ? directive.working !== false : travelling || !!(stop && item && bot.member.role === 'editor');
    bot.directed = !!(directive && directed);
    const floor = { slot, layoutVersion: 0 };
    let viewport: PresenceViewport, pose: CameraPose | null = null;
    if (phase.kind === 'floor') {
      const plan = phase.plan!;
      viewport = { kind: 'floor', centre: { ...plan.centre }, span: { ...plan.span }, following: null };
    } else if (phase.kind === 'orbit') {
      const orbit = phase.orbit!, az = mix(orbit.from, orbit.to, ease(t / (phase.end - phase.start)));
      const flat = Math.cos(orbit.elevation) * orbit.distance;
      const position = new THREE.Vector3(orbit.target.x + Math.sin(az) * flat, orbit.target.y + Math.sin(orbit.elevation) * orbit.distance, orbit.target.z + Math.cos(az) * flat);
      this.rig.position.copy(position); this.rig.up.set(0, 1, 0); this.rig.lookAt(orbit.target); this.rig.updateMatrixWorld();
      const forward = { x: orbit.target.x - position.x, z: orbit.target.z - position.z }, length = Math.hypot(forward.x, forward.z) || 1;
      viewport = { kind: 'orbit', camera: { position: position.toArray() as [number, number, number], quaternion: this.rig.quaternion.toArray() as [number, number, number, number],
        target: orbit.target.toArray() as [number, number, number], fov: 55, walkFov: null, yaw: bot.yaw, pitch: bot.pitch },
        wallMode: orbit.wallMode, showCeiling: false, isolatedRoom: null, following: null };
      pose = { mode: 'orbit', position: { x: position.x, z: position.z }, height: position.y, yaw: Math.atan2(forward.x / length, -forward.z / length),
        forward: { x: forward.x / length, z: forward.z / length }, target: { x: orbit.target.x, z: orbit.target.z } };
    } else {
      // The walker turns to what it points at, as a person looks where they point.
      const stand = phase.walk?.position ?? { x: 0, z: 0 }, aim = point ?? { x: stand.x + Math.sin(bot.yaw), y: EYE, z: stand.z + Math.cos(bot.yaw) };
      const distance = Math.hypot(aim.x - stand.x, aim.z - stand.z);
      const wantYaw = distance > .3 ? Math.atan2(aim.x - stand.x, aim.z - stand.z) : bot.yaw + dt * .35;
      const wantPitch = distance > .3 ? Math.max(-.7, Math.min(.2, Math.atan2(aim.y - EYE, distance))) : -.1;
      const k = dt ? 1 - Math.exp(-dt / .45) : t === 0 ? 1 : 0;
      bot.yaw = turn(bot.yaw, wantYaw, k); bot.pitch = mix(bot.pitch, wantPitch, k);
      this.rig.rotation.set(0, 0, 0); this.rig.rotateY(bot.yaw + Math.PI); this.rig.rotateX(bot.pitch); this.rig.updateMatrixWorld();
      const heading = { x: Math.sin(bot.yaw), z: Math.cos(bot.yaw) };
      viewport = { kind: 'walk', camera: { position: [stand.x, EYE, stand.z], quaternion: this.rig.quaternion.toArray() as [number, number, number, number],
        target: [stand.x + heading.x, EYE, stand.z + heading.z], fov: 70, walkFov: null, yaw: bot.yaw, pitch: bot.pitch },
        wallMode: 'full', showCeiling: true, isolatedRoom: null, following: null };
      pose = { mode: 'walk', position: { ...stand }, height: EYE, yaw: Math.atan2(heading.x, -heading.z), forward: heading, target: { x: stand.x + heading.x, z: stand.z + heading.z } };
    }
    // The guest looks around without pointing, so the demo shows the camera/avatar variant too.
    const captured = capturePresence({ kind: phase.kind }, level, bot.member.id === 'person:mason' && pose ? null : point, pose);
    return { protocol: 1, documentId: this.state.snapshot?.documentId ?? 'demo', generation: 1, clientId: bot.member.id, seq: ++bot.seq, floor,
      ...captured, viewport, selection, preview: null, userId: bot.member.id, displayName: bot.member.name, sentAt: Date.now() };
  }
}

const samePlace = (a: DemoTask, b: DemoTask): boolean => JSON.stringify([a.target, a.at]) === JSON.stringify([b.target, b.at]);

/**
 * Where a task's pointer rests: on an item its top, in a room the middle of its floor, on a wall its middle at eye
 * height. A plan reads x and z only, a 3D view draws the height too. Null when the target is gone.
 */
export function demoTargetPoint(target: DemoTarget, level: Level): WorldPoint | null {
  if (target.kind === 'point') return target.point;
  if (target.kind === 'item') { const item = level.items.find(candidate => candidate.id === target.id); return item ? itemPoint(item, false) : null; }
  if (target.kind === 'room') { const room = level.rooms.find(candidate => candidate.index === target.index); return room ? roomPoint(room) : null; }
  if (target.kind === 'opening') {
    const opening = level.openings.find(candidate => candidate.id === target.id);
    const wall = level.walls.find(candidate => candidate.id === opening?.wallId);
    return opening && wall ? { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset,
      y: opening.sill + opening.height / 2 } : null;
  }
  const wall = level.walls.find(candidate => candidate.id === target.id);
  return wall ? { x: (wall.start.x + wall.end.x) / 2, y: Math.min(1.3, wall.height / 2), z: (wall.start.z + wall.end.z) / 2 } : null;
}
